<template>
  <table :style="tableStyle" class="lb-table">
    <thead>
      <tr>
        <th v-if="hasSelection" class="lb-table__select-column">
          <LbTableCheckBox
            :value="areAllSelected"
            @check="selectAllRowsAndEmit"
            @unCheck="deSelectAllRowsAndEmit"
          />
        </th>
        <th v-for="column in columns" :key="column.property">
          {{ column.label }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="item in transformedData">
        <tr v-if="item.isHeading" :key="item.id">
          <td :colspan="columnsCount">
            {{ item.title }}
          </td>
        </tr>
        <tr v-else :key="item.id">
          <td v-if="hasSelection" class="lb-table__select-column">
            <LbTableCheckBox
              :value="isSelected(rowIdFn(item))"
              @check="selectRowAndEmit(item)"
              @unCheck="deSelectRowAndEmit(item)"
            />
          </td>
          <td v-for="column in columns" :key="column.property">
            <Component
              :is="column.component"
              v-if="column.component"
              :row="item"
            />
            <LbTableDefaultColumn v-else :value="item[column.property]" />
          </td>
        </tr>
      </template>
    </tbody>

    <colgroup>
      <col v-if="hasSelection" :style="{ width: `${checkboxWidth}px` }" />
      <col
        v-for="column in columns"
        :key="column.property"
        span="1"
        :style="{ width: column.width + 'px' }"
      />
    </colgroup>
  </table>
</template>

<script>
import LbTableCheckBox from './LbTableCheckBox';
import LbTableDefaultColumn from './LbTableDefaultColumn';

const CHECKBOX_WIDTH = 45; // in px

export default {
  name: 'LbTable',
  components: {
    LbTableCheckBox,
    LbTableDefaultColumn,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    hasSelection: {
      type: Boolean,
      default: false,
    },
    selection: {
      type: Array,
      default: () => [],
    },
    rowIdFn: {
      type: Function,
      required: true,
    },
    clearSelectionController: {
      type: Object,
      default: () => ({ onClearSelection() {}, removeOnClearSelection() {} }),
    },
  },
  data() {
    return {
      selectedRows: {},
    };
  },
  computed: {
    columnsCount() {
      const columnsCountWithoutSelection = this.columns.length;
      return this.hasSelection
        ? columnsCountWithoutSelection + 1 // Plus 1 which is the selection column
        : columnsCountWithoutSelection;
    },
    transformedData() {
      const transformedData = [];
      this.data.forEach(item => {
        if (!Array.isArray(item.data)) {
          transformedData.push(item);
          return;
        }

        transformedData.push(
          {
            title: item.title,
            isHeading: true,
          },
          ...item.data,
        );
      });
      return transformedData;
    },
    transformedDataWithoutHeadings() {
      return this.transformedData.filter(({ isHeading }) => !isHeading);
    },
    checkboxWidth() {
      return CHECKBOX_WIDTH;
    },
    tableMinWidth() {
      return this.columns.reduce(
        (acc, { width = 0 }) => acc + width,
        CHECKBOX_WIDTH,
      );
    },
    tableStyle() {
      return { minWidth: this.tableMinWidth + 'px' };
    },
    areAllSelected() {
      const setOfData = new Set(this.transformedDataWithoutHeadings);
      if (Object.keys(this.selectedRows).length !== setOfData.size)
        return false;
      for (let selectedRowItem of Object.values(this.selectedRows)) {
        if (!setOfData.has(selectedRowItem)) return false;
      }
      return true;
    },
    isSelected() {
      return id => {
        return Boolean(this.selectedRows[id]);
      };
    },
  },
  watch: {
    selection() {
      this.deSelectAllRows();
      this.selection.forEach(item => {
        this.selectRow(item);
      });
    },
    data(newVal, prevVal) {
      if (newVal.length !== prevVal.length) {
        this.clearSelection();
      }
    },
  },
  mounted() {
    this.clearSelectionController.onClearSelection(this.clearSelection);
  },
  beforeDestroy() {
    this.clearSelectionController.removeOnClearSelection(this.clearSelection);
  },
  methods: {
    clearSelection() {
      this.deSelectAllRowsAndEmit();
    },
    selectAllRowsAndEmit() {
      this.deSelectAllRows();
      this.transformedDataWithoutHeadings.forEach(item => {
        this.selectRow(item);
      });
      this.emitSelectedRows();
    },
    deSelectAllRows() {
      this.selectedRows = {};
    },
    deSelectAllRowsAndEmit() {
      this.deSelectAllRows();
      this.emitSelectedRows();
    },
    selectRow(item) {
      this.$set(this.selectedRows, this.rowIdFn(item), item);
    },
    selectRowAndEmit(item) {
      this.selectRow(item);
      this.emitSelectedRows();
    },
    deSelectRow(item) {
      this.$delete(this.selectedRows, this.rowIdFn(item));
    },
    deSelectRowAndEmit(item) {
      this.deSelectRow(item);
      this.emitSelectedRows();
    },
    emitSelectedRows() {
      this.$emit('update:selection', Object.values(this.selectedRows));
    },
  },
};
</script>

<style lang="scss">
.lb-table {
  $border: 1px solid #dfdfdf;

  width: 100%;
  border-collapse: collapse;
  font-size: rem-calc(13px);

  th,
  td {
    padding: $global-padding / 1.5 $global-padding / 2;
  }

  tr {
    border: $border;
  }

  thead {
    background: #f4f5f6;

    th {
      border: $border;
      font-weight: 400;
      color: #909399;
    }
  }

  & &__select-column {
    text-align: center;
  }

  &__rating-column {
    text-align: center;
  }
}
</style>
